/**
 * The strict Zod mirror of `Career` (src/career/types.ts), used by saves (M2-12): unknown keys fail at every
 * level, numbers must be finite, and the invariants the commands keep (non-negative money and tonnes,
 * standings within ±100) are checked so a corrupt save is named instead of loaded.
 *
 * FROZEN after wave 1 (plan §1.2): an optional member added to types.ts gets an optional key here.
 *
 * M8 (MC-0a, docs/plans/2026-10-04-m8-materials-craft.md): every M8 career key is added here once and is optional,
 * so older saves load unchanged with no save version. ROLLBACK RISK: the schema is strict, so a build from before
 * MC-0a refuses a career that carries any of `storage`, `fabOrders`, `patterns`, `blueprints`, `priceLog`, or a
 * component's or salvaged module's `provenance`/`maker` (each lane-K deploy notes it).
 *
 * M9 (CH-2b, docs/plans/2026-10-05-m9-claimable-hulls.md §2, §5.1): `hulks` and `OwnedShip.flaws`/`valueMult` are
 * optional the same way, with no save version. ROLLBACK RISK: a build from before CH-2b refuses a career that
 * carries `hulks`, or a hangar ship with `flaws` or `valueMult` (the M9 landing notes it).
 *
 * M10 P3b2 (plan `docs/plans/2026-10-11-m10-p3.md` question 5): `hulkTable` is optional the same way. It is written
 * only when the build's newest hulk table is above 1, so no P3 career carries it (saves byte-identical) and a build
 * from before P3b2 still loads every P3 career. ROLLBACK RISK from P4b's table 2 on: an older build refuses a career
 * that carries `hulkTable`. A career whose `hulkTable` is above this build's newest table is refused here.
 *
 * M10 P4a4 (plan `docs/plans/2026-10-11-m10-p4a.md` P4a4): `makerStanding` is optional the same way (record v3
 * unchanged). It is written only after a hull purchase gains standing. ROLLBACK RISK: a build from before P4a4
 * refuses a career that carries it, so roll forward only; the rollback is the data switch (`makerStanding.json
 * perHull` 0; review m-8).
 *
 * M10 P5a0 (plan `docs/plans/2026-10-17-m10-p5a.md` P5a0 and its review changes): the P5a career keys
 * (`OwnedShip.name`/`history`/`hold`/`cradle`, a hold's or pod's `processed`, `Career.pinnedHull`/`parkedL`/
 * `carriedBy`) are optional the same way (career v1, record v3 unchanged), and inert until their cards write them.
 * ROLLBACK RISK: a build from before P5a0 refuses a career that carries any of them, so roll forward only.
 */
import { z } from 'zod'
import {
  COMPONENT_TYPES,
  MK_COMPONENT_TYPES,
  WARE_IDS,
  componentTypeSchema,
  contractTypeSchema,
  moduleIdSchema,
  parseModuleId,
  simFactionIdSchema,
  slotIdSchema,
  wareIdSchema,
  type ModuleKind,
} from '../data/m2Schemas'
import { hulksConfig, latestHulkTable } from '../hulks/data'
import { incomeMixSchema, storyStateSchema, tetherTotalsSchema } from '../story/schemas'
import { FACTION_IDS, PLAYER_FACTION_ID } from '../strategic/types'
import { WARE_COUNT as STRATEGIC_WARE_COUNT } from '../strategic/wares'
import { GOOD_IDS, goodsSchema } from './parts'
import {
  HULK_CLEARED,
  HULL_EVENT_KINDS,
  HULL_HISTORY_MAX,
  LEASE_EVENT_KINDS,
  MAKER_IDS,
  PARKED_L_ORDERS,
  PATTERN_UNLOCK_IDS,
  SHIP_NAME_MAX_CHARS,
  type BerthEntry,
  type BlueprintState,
  type CargoComponent,
  type CargoContents,
  type CargoState,
  type Career,
  type FabOrder,
  type HulkSite,
  type HulkState,
  type HullEvent,
  type MakerId,
  type MkComponentType,
  type OwnedShip,
  type PatternState,
  type PatternUnlockId,
  type PriceLogEntry,
  type SalvagedModule,
  type SalvagedState,
} from './types'

const nonNegative = z.number().nonnegative()
const unit = z.number().min(0).max(1)
const id = z.string().min(1)
const vec3Tuple = z.tuple([z.number(), z.number(), z.number()])
/** M9 (CH-2b): a flaw bitmask over `hulks.json`'s flaw list; the worker's `FleetStore.flaws` is a Uint16. */
const flawMask = z.number().int().min(0).max(0xffff)
/** M9 (CH-2b): a claimed hull's value factor, in (0, 1] (1 = pristine). */
const valueMultSchema = z.number().gt(0).max(1)

const waresSchema = z.record(wareIdSchema, nonNegative)

// Exhaustiveness: `MAKER_IDS` and `PATTERN_UNLOCK_IDS` name every member of their unions.
type Missing<U, L extends readonly string[]> = Exclude<U, L[number]>
const exhaustive: [Missing<MakerId, typeof MAKER_IDS>, Missing<PatternUnlockId, typeof PATTERN_UNLOCK_IDS>] extends [never, never] ? true : false = true
void exhaustive

/** M8 (MC-0a): who made a component (`MakerId`). */
export const makerIdSchema = z.enum(MAKER_IDS)
/** M8 (MC-0a): the Rush-pattern provenance tag (MC-h1). */
const provenanceSchema = z.literal('rush')

export const cargoComponentSchema: z.ZodType<CargoComponent> = z.lazy(() =>
  z.strictObject({
    id,
    type: componentTypeSchema,
    condition: unit,
    mk: z.number().int().min(1).max(3).optional(),
    provenance: provenanceSchema.optional(),
    maker: makerIdSchema.optional(),
    massT: nonNegative,
    value: nonNegative,
    contractId: id.optional(),
    sealed: z.boolean().optional(),
    story: z.boolean().optional(),
    contraband: z.boolean().optional(),
    contents: cargoContentsSchema.optional(),
  }),
)

/** M10 P5a0 (Q4): processed tonnes per ware (a hold's or a pod's `processed`). */
const processedSchema = z.partialRecord(wareIdSchema, nonNegative)

/** M10 P5a0 (Q4): a ware's processed tonnes are some of its tonnes, never more. */
function checkProcessed(hold: { wares: Partial<Record<string, number>>; processed?: Partial<Record<string, number>> | undefined }, ctx: z.RefinementCtx): void {
  const processed = hold.processed
  if (processed === undefined) return
  for (const [w, t] of Object.entries(processed)) {
    if (t !== undefined && t > (hold.wares[w] ?? 0)) {
      ctx.addIssue({ code: 'custom', path: ['processed', w], message: `${t} t of ${w} processed, but the hold has ${hold.wares[w] ?? 0} t` })
    }
  }
}

export const cargoContentsSchema: z.ZodType<CargoContents> = z.lazy(() =>
  // `goods` (SY-2b) is optional: a pod saved before goods rode in the death pod loads with no migration.
  z
    .strictObject({
      wares: waresSchema,
      components: z.array(cargoComponentSchema),
      goods: goodsSchema.optional(),
      // M10 P5a0 (Q4): optional; absent = none processed.
      processed: processedSchema.optional(),
    })
    .superRefine(checkProcessed),
)

/** The hold (`Career.cargo`; M10 P5a0 also `OwnedShip.hold`). */
export const cargoStateSchema: z.ZodType<CargoState> = z
  .strictObject({
    capacityT: nonNegative,
    wares: waresSchema,
    components: z.array(cargoComponentSchema),
    // The goods block (SY-2a): a partial record of floats, so a pre-goods save (three whole relay-part counts) loads.
    parts: goodsSchema.optional(),
    // M10 P5a0 (Q4): optional; absent = none processed (every hold from before P5a).
    processed: processedSchema.optional(),
  })
  .superRefine(checkProcessed)

/** M10 P5a0: a ship name is 1 to `SHIP_NAME_MAX_CHARS` characters with no outer whitespace. */
export const shipNameSchema = z
  .string()
  .min(1)
  .max(SHIP_NAME_MAX_CHARS)
  .refine((s) => s === s.trim(), { message: 'a ship name has no leading or trailing whitespace' })

/** M10 P5a0: one hull event (P5a3b). */
export const hullEventSchema: z.ZodType<HullEvent> = z.strictObject({
  kind: z.enum(HULL_EVENT_KINDS),
  day: z.number().int().nonnegative(),
  stationId: id.optional(),
})

/**
 * The module kinds each installable component type fits as (economy plan SY-5a): reactor → power, shieldGen →
 * shield, thruster → thrusters, weapon → pdc or laser (the caller picks; pdc first).
 */
export const SALVAGED_MODULE_KINDS: Readonly<Record<MkComponentType, readonly ModuleKind[]>> = {
  reactor: ['power'],
  weapon: ['pdc', 'laser'],
  shieldGen: ['shield'],
  thruster: ['thrusters'],
}

/** A salvaged module (SY-5a): its `moduleId` must be a kind its component type fits as, at its `mk`. */
export const salvagedModuleSchema: z.ZodType<SalvagedModule> = z
  .strictObject({
    moduleId: moduleIdSchema,
    sourceId: id,
    type: z.enum(MK_COMPONENT_TYPES),
    condition: unit,
    mk: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    // M8 (MC-0a): the source component's tags, kept through install, uninstall and ship switching.
    provenance: provenanceSchema.optional(),
    maker: makerIdSchema.optional(),
  })
  .superRefine((m, ctx) => {
    // A salvaged component installs as its base module, never its maker's pattern (MC-f2c, review 1 m15).
    const { kind, mk, pattern } = parseModuleId(m.moduleId)
    if (mk !== m.mk || pattern !== undefined || !SALVAGED_MODULE_KINDS[m.type].includes(kind)) {
      ctx.addIssue({ code: 'custom', path: ['moduleId'], message: `${m.moduleId} is not what a Mk ${m.mk} ${m.type} installs as` })
    }
  })

/**
 * Salvaged-module provenance (SY-5a), exported so M6's `OwnedShip.salvaged?` reuses it (M6I-5). Source ids are
 * unique across the locker and the fitted slots.
 */
export const salvagedStateSchema: z.ZodType<SalvagedState> = z
  .strictObject({
    locker: z.array(salvagedModuleSchema),
    fitted: z.partialRecord(slotIdSchema, salvagedModuleSchema),
  })
  .superRefine((st, ctx) => {
    const seen = new Set<string>()
    for (const m of [...st.locker, ...Object.values(st.fitted)]) {
      if (m === undefined) continue
      if (seen.has(m.sourceId)) ctx.addIssue({ code: 'custom', path: ['locker'], message: `duplicate salvaged source id ${m.sourceId}` })
      seen.add(m.sourceId)
    }
  })

/** Every `salvaged.fitted` slot names the module fitted there (SY-5a's ship rule, shared with the hangar). */
function checkSalvagedFit(
  ship: { fitted: Partial<Record<string, string>>; salvaged?: SalvagedState | undefined },
  ctx: z.RefinementCtx,
): void {
  const fitted = ship.salvaged?.fitted ?? {}
  for (const slot of slotIdSchema.options) {
    const m = fitted[slot]
    if (m !== undefined && m.moduleId !== ship.fitted[slot]) {
      ctx.addIssue({ code: 'custom', path: ['salvaged', 'fitted', slot], message: `salvaged ${m.moduleId} is not the module fitted in ${slot}` })
    }
  }
}

/**
 * An owned fleet ship in the Career's hangar (M6 plan P21). `salvaged` reuses SY-5a's `salvagedStateSchema`
 * (economy plan M6I-5) and is optional, so a hangar written before it loads. M9 (CH-2b) adds the optional claimed-hull
 * `flaws` bitmask and `valueMult` (CH-8 writes them); absent = none and 1.
 */
export const ownedShipSchema: z.ZodType<OwnedShip> = z
  .strictObject({
    fleetId: id,
    defId: id,
    fitted: z.record(slotIdSchema, moduleIdSchema),
    locker: z.array(moduleIdSchema),
    insured: z.boolean(),
    escapePod: z.boolean(),
    salvaged: salvagedStateSchema.optional(),
    // M7 S37: the resonance meter travels with its ship.
    resonanceMeter: z.boolean().optional(),
    // M6 P24 (switch): what a left ship keeps; absent = full, or not the parked lease ship.
    hull: nonNegative.optional(),
    torpedoes: z.number().int().nonnegative().optional(),
    stationId: id.optional(),
    // M6 P24b: the Career station id the parked lease ship was left at (the total-loss respawn).
    dockedAt: id.optional(),
    // M9 (CH-2b): a claimed hull's flaws and value factor; absent = none and 1 (pre-M9 hangars load unchanged).
    flaws: flawMask.optional(),
    valueMult: valueMultSchema.optional(),
    // M10 P5a0: optional; absent = the default name, no history, an empty hold, no cradle.
    name: shipNameSchema.optional(),
    history: z.array(hullEventSchema).max(HULL_HISTORY_MAX).optional(),
    hold: cargoStateSchema.optional(),
    cradle: z.strictObject({ carried: id.nullable() }).optional(),
  })
  .superRefine(checkSalvagedFit)

/** The hangar: fleet ids are unique. */
export const hangarSchema: z.ZodType<OwnedShip[]> = z.array(ownedShipSchema).superRefine((ships, ctx) => {
  const seen = new Set<string>()
  for (let i = 0; i < ships.length; i++) {
    const fid = ships[i].fleetId
    if (seen.has(fid)) ctx.addIssue({ code: 'custom', path: [i, 'fleetId'], message: `duplicate hangar fleet id ${fid}` })
    seen.add(fid)
  }
})

export const leaseEventSchema = z.strictObject({ t: nonNegative, kind: z.enum(LEASE_EVENT_KINDS), amount: nonNegative })

export const leaseStateSchema = z.strictObject({
  principal: nonNegative,
  ratePerCycle: unit,
  payment: nonNegative,
  cycleSeconds: z.number().positive(),
  nextDueAt: nonNegative,
  missed: z.number().int().nonnegative(),
  arrears: nonNegative,
  feesDue: nonNegative,
  collections: z.enum(['none', 'notice', 'repo']),
  repoPausedUntil: nonNegative,
  owned: z.boolean(),
  insured: z.boolean(),
  history: z.array(leaseEventSchema),
})

export const contractStateSchema = z.strictObject({
  id,
  type: contractTypeSchema,
  station: id,
  issuedCycle: z.number().int().nonnegative(),
  acceptedAt: nonNegative,
  deadlineAt: nonNegative,
  reward: nonNegative,
  standing: z.number(),
  premium: z.boolean().optional(),
  params: z.strictObject({
    ware: wareIdSchema.optional(),
    tons: nonNegative.optional(),
    destination: id.optional(),
    marker: vec3Tuple.optional(),
    componentType: componentTypeSchema.optional(),
    podId: id.optional(),
    tier: z.number().int().nonnegative().optional(),
    ringId: id.optional(),
    relayId: id.optional(),
    systemId: id.optional(),
    units: z.number().int().positive().optional(),
    delivered: z.number().int().nonnegative().optional(),
    targetName: z.string().optional(),
    good: z.enum(GOOD_IDS).optional(),
    siteId: id.optional(),
  }),
  status: z.enum(['active', 'ready']),
})

// ---------------------------------------------------------------------------------------------
// M8 Materials & Craft keys (MC-0a): optional, so no save version
// ---------------------------------------------------------------------------------------------

/** A berth (MC-b4): the owner at first deposit (a strategic faction or the player's corp) and what it holds. */
export const berthEntrySchema: z.ZodType<BerthEntry> = z.strictObject({
  owner: z.enum([...FACTION_IDS, PLAYER_FACTION_ID]),
  goods: goodsSchema,
  wares: z.partialRecord(wareIdSchema, nonNegative),
  components: z.array(cargoComponentSchema),
  relocateAfter: nonNegative.optional(),
})

/** A good or a raw ware (a fab input). */
const goodOrWareSchema = z.enum([...GOOD_IDS, ...WARE_IDS])
const tonnesByInput = z.partialRecord(goodOrWareSchema, nonNegative)

/** A fab order (MC-c3a/c3b). */
export const fabOrderSchema: z.ZodType<FabOrder> = z.strictObject({
  id,
  moduleId: moduleIdSchema,
  stationId: id,
  startedAt: nonNegative,
  paid: nonNegative,
  required: tonnesByInput,
  delivered: tonnesByInput,
  carry: z.strictObject({ id: z.enum([...GOOD_IDS, ...WARE_IDS, ...COMPONENT_TYPES]), need: nonNegative, have: nonNegative }),
  salvageIn: z.array(id),
})

/** Open fab orders: ids are unique. */
export const fabOrdersSchema: z.ZodType<FabOrder[]> = z.array(fabOrderSchema).superRefine((orders, ctx) => {
  const seen = new Set<string>()
  for (let i = 0; i < orders.length; i++) {
    if (seen.has(orders[i].id)) ctx.addIssue({ code: 'custom', path: [i, 'id'], message: `duplicate fab order id ${orders[i].id}` })
    seen.add(orders[i].id)
  }
})

/** The one pattern-unlock store (MC-f3, MC-h5a): unique ids. */
export const patternStateSchema: z.ZodType<PatternState> = z
  .strictObject({ unlocked: z.array(z.enum(PATTERN_UNLOCK_IDS)) })
  .superRefine((p, ctx) => {
    if (new Set(p.unlocked).size !== p.unlocked.length) ctx.addIssue({ code: 'custom', path: ['unlocked'], message: 'duplicate pattern unlock' })
  })

export const blueprintStateSchema: z.ZodType<BlueprintState> = z.strictObject({ fragments: z.number().int().nonnegative() })

/** One row per strategic ware: a whole credit price or a small station index, −1 = no data. */
const priceRow = z.array(z.number().int().min(-1)).length(STRATEGIC_WARE_COUNT)

/** A system's logged best prices (TR-1a), `WARE_COUNT` long per row (MC-e5b remaps it at the ware re-stride). */
export const priceLogEntrySchema: z.ZodType<PriceLogEntry> = z.strictObject({
  t: nonNegative,
  b: priceRow,
  s: priceRow,
  bAt: priceRow,
  sAt: priceRow,
})

// ---------------------------------------------------------------------------------------------
// M9 Claimable hulls keys (CH-2b): optional, so no save version
// ---------------------------------------------------------------------------------------------

/** A hulk the pilot knows (CH-2b): found or surveyed; the flaw roll only once surveyed. */
export const hulkSiteSchema: z.ZodType<HulkSite> = z
  .strictObject({
    hull: id,
    found: z.literal(true).optional(),
    surveyed: nonNegative.optional(),
    flaws: flawMask.optional(),
    vented: z.literal(true).optional(),
    cleared: z.enum(HULK_CLEARED).optional(),
    open: z.literal(true).optional(),
    kit: z
      .strictObject({
        components: z.partialRecord(componentTypeSchema, z.number().int().nonnegative()),
        goods: goodsSchema,
      })
      .optional(),
    claimedAt: nonNegative.optional(),
  })
  .superRefine((site, ctx) => {
    if (site.found !== true && site.surveyed === undefined) ctx.addIssue({ code: 'custom', path: ['found'], message: 'a hulk site is found or surveyed' })
    if (site.flaws !== undefined && site.surveyed === undefined) ctx.addIssue({ code: 'custom', path: ['flaws'], message: 'flaws are rolled by the survey' })
  })

/** The Career's hulks (CH-2b): `done` keys are unique and a finished hulk keeps no site. */
export const hulkStateSchema: z.ZodType<HulkState> = z
  .strictObject({ done: z.array(id), sites: z.record(id, hulkSiteSchema) })
  .superRefine((h, ctx) => {
    const seen = new Set<string>()
    for (let i = 0; i < h.done.length; i++) {
      const key = h.done[i]
      if (seen.has(key)) ctx.addIssue({ code: 'custom', path: ['done', i], message: `duplicate done hulk ${key}` })
      if (Object.hasOwn(h.sites, key)) ctx.addIssue({ code: 'custom', path: ['sites', key], message: `finished hulk ${key} still has a site` })
      seen.add(key)
    }
  })

export const careerSchema: z.ZodType<Career> = z.strictObject({
  version: z.literal(1),
  seed: z.number().int().min(0).max(0xffffffff),
  gameTime: nonNegative,
  wallet: nonNegative,
  debt: nonNegative,
  lease: leaseStateSchema,
  cargo: cargoStateSchema,
  ship: z
    .strictObject({
      defId: id,
      hull: nonNegative,
      torpedoes: z.number().int().nonnegative(),
      fitted: z.record(slotIdSchema, moduleIdSchema),
      locker: z.array(moduleIdSchema),
      // SY-5a: optional; absent = no salvaged modules (pre-SY-5a saves load unchanged).
      salvaged: salvagedStateSchema.optional(),
      // M7 S37: the resonance meter utility module; optional, so older saves load unchanged.
      resonanceMeter: z.boolean().optional(),
    })
    .superRefine(checkSalvagedFit),
  standings: z.record(simFactionIdSchema, z.number().min(-100).max(100)),
  contracts: z.strictObject({ active: z.array(contractStateSchema), completedIds: z.array(id), failedIds: z.array(id) }),
  dataCores: z.array(id),
  lastDockedStation: id,
  stats: z.strictObject({
    tetherstoneT: nonNegative,
    oreT: nonNegative,
    partsSecured: z.number().int().nonnegative(),
    deaths: z.number().int().nonnegative(),
    earned: nonNegative,
  }),
  storyTier: z.number().int().min(0).max(5).optional(),
  guardianKills: z
    .array(
      z.strictObject({
        day: z.number().int().nonnegative(),
        systemId: z.string(),
        shipId: z.string(),
        hull: z.string().optional(),
        pilotSeed: z.number().int().min(0).max(0xffffffff).optional(),
        selfDefence: z.boolean().optional(),
      }),
    )
    .optional(),
  tetherSales: z
    .array(
      z.strictObject({
        day: z.number().int().nonnegative(),
        stationId: id,
        tonnes: nonNegative,
        credits: nonNegative,
        bunker: z.boolean().optional(),
      }),
    )
    .optional(),
  // M7 (S0): optional; absent = none (pre-M7 saves load unchanged). The story block fills its own defaults.
  tetherTotals: tetherTotalsSchema.optional(),
  incomeMix: incomeMixSchema.optional(),
  story: storyStateSchema.optional(),
  // M6 (P21): optional; absent = no fleet ships (pre-M6 saves load unchanged).
  hangar: hangarSchema.optional(),
  flownShip: id.optional(),
  // M8 (MC-0a): optional; absent = none (pre-M8 saves load unchanged). See the rollback note at the top.
  storage: z.record(id, berthEntrySchema).optional(),
  fabOrders: fabOrdersSchema.optional(),
  patterns: patternStateSchema.optional(),
  blueprints: blueprintStateSchema.optional(),
  priceLog: z.record(id, priceLogEntrySchema).optional(),
  // M9 (CH-2b): optional; absent = no hulks known (pre-M9 saves load unchanged). See the rollback note at the top.
  hulks: hulkStateSchema.optional(),
  // M10 P3b2: optional, integer ≥ 2 (absent = 1; `newCareer` never writes 1). See the rollback note at the top.
  hulkTable: z.number().int().min(2).optional(),
  // M10 P4a4: optional; absent = no purchase standing gained. See the rollback note at the top.
  makerStanding: z
    .strictObject({
      window: z.number().int().min(0),
      gained: z.partialRecord(simFactionIdSchema, z.number().finite().min(0)),
    })
    .optional(),
  // M10 P5a0: optional; absent = nothing pinned, no L parked, not carried. See the rollback note at the top.
  pinnedHull: z.strictObject({ hull: id, stationId: id, posted: z.array(id).optional() }).optional(),
  parkedL: z
    .strictObject({
      fleetId: id,
      systemId: id,
      // Float64 world metres (D-013); `z.number()` refuses NaN and the infinities.
      pos: vec3Tuple,
      rot: z.tuple([z.number(), z.number(), z.number(), z.number()]),
      order: z.enum(PARKED_L_ORDERS),
    })
    .optional(),
  carriedBy: id.optional(),
}).superRefine((career, ctx) => {
  // M10 P5a0: the carrying L and the parked L are hangar ships.
  const inHangar = (fleetId: string): boolean => (career.hangar ?? []).some((s) => s.fleetId === fleetId)
  if (career.carriedBy !== undefined && !inHangar(career.carriedBy)) {
    ctx.addIssue({ code: 'custom', path: ['carriedBy'], message: `carriedBy ${career.carriedBy} names no hangar ship` })
  }
  if (career.parkedL !== undefined && !inHangar(career.parkedL.fleetId)) {
    ctx.addIssue({ code: 'custom', path: ['parkedL', 'fleetId'], message: `parkedL ${career.parkedL.fleetId} names no hangar ship` })
  }
  // M10 P3b2 (D-751 no-downgrade): a career from a build with a newer hulk table than this one is refused on load.
  if (career.hulkTable === undefined) return
  const latest = latestHulkTable(hulksConfig())
  if (career.hulkTable > latest) {
    ctx.addIssue({
      code: 'custom',
      path: ['hulkTable'],
      message: `hulkTable ${career.hulkTable} is from a newer build (this build has hulk tables up to ${latest}); update the game to load this career`,
    })
  }
})
